import React, { useState, useEffect } from 'react';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { BarChart, Bar, PieChart, Pie, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell, ResponsiveContainer } from 'recharts';
import { format, subDays } from 'date-fns';
import { WASTE_CATEGORIES, getCategoryColor } from '../config/wasteCategories';
import { FaTrash, FaCheckCircle, FaClock } from 'react-icons/fa';
import './Dashboard.css';

const Dashboard = () => {
  useAuth();
  const [wasteReports, setWasteReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(7);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const reports = await api.getWasteReports();
        setWasteReports(reports);
      } catch (error) {
        console.error('Error fetching reports:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, [dateRange]);

  const filteredReports = wasteReports.filter(report => {
    const reportDate = new Date(report.timestamp);
    const cutoffDate = subDays(new Date(), dateRange);
    return reportDate >= cutoffDate;
  });

  const totalReports = filteredReports.length;
  const cleanedReports = filteredReports.filter(r => r.status === 'cleaned').length;
  const pendingReports = totalReports - cleanedReports;

  const cleanedWithDuration = filteredReports.filter(r => r.cleanupEvidence?.duration);
  const avgCleanupHours = cleanedWithDuration.length > 0
    ? (cleanedWithDuration.reduce((sum, r) => sum + r.cleanupEvidence.duration.totalHours, 0) / cleanedWithDuration.length).toFixed(1)
    : 0;

  const categoryData = Object.keys(WASTE_CATEGORIES).map(key => {
    const count = filteredReports.filter(r => r.category === key).length;
    return {
      name: WASTE_CATEGORIES[key].name,
      value: count,
      color: getCategoryColor(key)
    };
  }).filter(item => item.value > 0);

  const wasteTypeCount = {};
  filteredReports.forEach(report => {
    wasteTypeCount[report.wasteType] = (wasteTypeCount[report.wasteType] || 0) + 1;
  });
  const wasteTypeData = Object.entries(wasteTypeCount)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  const userReportCount = {};
  filteredReports.forEach(report => {
    userReportCount[report.reportedBy] = (userReportCount[report.reportedBy] || 0) + 1;
  });
  const userReportData = Object.entries(userReportCount)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const date = subDays(new Date(), 6 - i);
    return format(date, 'MMM dd');
  });

  const dailyData = last7Days.map(dateStr => {
    const count = filteredReports.filter(report => {
      const reportDateStr = format(new Date(report.timestamp), 'MMM dd');
      return reportDateStr === dateStr;
    }).length;
    return { date: dateStr, reports: count };
  });

  const evidenceTypes = ['photo', 'video', 'audio', 'text'];
  const evidenceData = evidenceTypes.map(type => {
    const count = filteredReports.filter(r => r.evidenceType === type).length;
    return { name: type.charAt(0).toUpperCase() + type.slice(1), count };
  }).filter(item => item.count > 0);

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>Dashboard</h1>
          <div className="date-range-selector">
            <label>Date Range:</label>
            <select value={dateRange} onChange={(e) => setDateRange(Number(e.target.value))}>
              <option value={7}>Last 7 Days</option>
              <option value={30}>Last 30 Days</option>
              <option value={90}>Last 90 Days</option>
              <option value={365}>Last Year</option>
            </select>
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#3498db' }}>
              <FaTrash />
            </div>
            <div className="stat-content">
              <h3>{totalReports}</h3>
              <p>Total Reports</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#2ecc71' }}>
              <FaCheckCircle />
            </div>
            <div className="stat-content">
              <h3>{cleanedReports}</h3>
              <p>Cleaned Up</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#e74c3c' }}>
              <FaClock />
            </div>
            <div className="stat-content">
              <h3>{pendingReports}</h3>
              <p>Pending</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#f39c12' }}>
              <FaClock />
            </div>
            <div className="stat-content">
              <h3>{avgCleanupHours}h</h3>
              <p>Avg. Cleanup Time</p>
            </div>
          </div>
        </div>

        <div className="charts-grid">
          <div className="chart-card full-width">
            <h3>Daily Trend (Last 7 Days)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="reports" stroke="#2ecc71" strokeWidth={3} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card">
            <h3>Waste by Category</h3>
            {categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="no-data">No data available</p>
            )}
          </div>

          <div className="chart-card">
            <h3>Evidence Type Distribution</h3>
            {evidenceData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={evidenceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3498db" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="no-data">No data available</p>
            )}
          </div>

          <div className="chart-card">
            <h3>Top 10 Waste Types</h3>
            {wasteTypeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={wasteTypeData} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={120} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#e74c3c" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="no-data">No data available</p>
            )}
          </div>

          <div className="chart-card">
            <h3>Top 10 Contributors</h3>
            {userReportData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={userReportData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#2ecc71" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="no-data">No data available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
